import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { DAILY_SUMMARY } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { DAILY_SUMMARY_LIST_PATH } from '../../navigations/path';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString();
};

const ViewDailySummary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!id) return;
    const fetchOne = async () => {
      setLoading(true);
      try {
        const { data: response } = await api.get(DAILY_SUMMARY.BY_ID(id), {
          params: { 'relations[]': ['agent', 'assignedNumber'] },
        });
        const item = response?.data ?? response ?? {};
        setData(item);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load daily summary');
        navigate(DAILY_SUMMARY_LIST_PATH);
      } finally {
        setLoading(false);
      }
    };
    fetchOne();
  }, [id, navigate]);

  if (loading) {
    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
          <CircularProgress color="primary" />
        </Container>
      </PageLayout>
    );
  }

  if (!data) {
    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Typography color="error">Daily summary not found.</Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(DAILY_SUMMARY_LIST_PATH)}
            sx={{ mt: 2 }}
          >
            Back to List
          </Button>
        </Container>
      </PageLayout>
    );
  }

  const InfoItem = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value ?? '—'}
      </Typography>
    </Grid>
  );

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            View Daily Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(DAILY_SUMMARY_LIST_PATH)}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/daily-summary/edit/${id}`)}
            >
              Edit
            </Button>
          </Box>
        </Box>

        <Card variant="outlined" sx={{ boxShadow: 1 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.secondary' }}>
              Agent Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <InfoItem label="Agent Name" value={data.agent?.name} />
              <InfoItem label="Date" value={formatDate(data.report_date ?? data.date)} />
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.secondary' }}>
              Call Statistics
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <InfoItem label="No. of Dials" value={data.no_of_dials} />
              <InfoItem label="Answered Calls" value={data.answered_calls} />
              <InfoItem label="Call Duration" value={data.call_duration} />
              <InfoItem label="Interested Leads (> 1 Mint)" value={data.interested_leads} />
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.secondary' }}>
              Additional Information
            </Typography>
            <Grid container spacing={2}>
              <InfoItem label="Dial From / Assign Number" value={data.assign_number ?? data.assigned_number?.name ?? data.dial_from_number} />
              <InfoItem label="Created At" value={formatDate(data.created_at)} />
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewDailySummary;
